import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileSearch, ScanLine, Building2, Clock, PackageCheck, Landmark } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const fmtDT = (v) => {
  if (!v) return '—';
  let s = String(v);
  // Treat tz-less ISO strings as UTC so they render consistently in local time.
  if (!/[zZ]|[+-]\d\d:?\d\d$/.test(s)) s = s.replace(' ', 'T') + 'Z';
  try {
    return new Date(s).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch { return String(v); }
};

const FetchDocumentNumber = ({ backRoute = '/' }) => {
  const navigate = useNavigate();
  const [bundle, setBundle] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {bundle_barcode, found, rows}
  const inputRef = useRef(null);

  const doSearch = async () => {
    const code = bundle.trim();
    if (!code) { toast.error('Enter or scan a bundle number'); return; }
    setLoading(true);
    try {
      const res = await apiClient.get('/accounts/document-by-bundle', { params: { bundle_barcode: code } });
      setResult(res.data);
      if (!res.data.found) toast.warning('No inward document found for this bundle');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Lookup failed');
      setResult(null);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const rows = result?.rows || [];

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(backRoute)} className="text-[#F9FAFB] hover:bg-white/10" data-testid="fdn-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <FileSearch className="w-6 h-6" />
            <div>
              <h1 className="text-xl font-bold font-display">Fetch Document Number</h1>
              <p className="text-sm text-[#E8DCC8]">Scan a bundle to trace its inward document across all modules</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-4 mb-6">
          <label className="text-sm font-medium text-[#1A1F2E] flex items-center gap-2 mb-2">
            <ScanLine className="w-4 h-4" /> Bundle Number
          </label>
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              autoFocus
              value={bundle}
              onChange={(e) => setBundle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !loading && doSearch()}
              placeholder="Scan / type bundle barcode + Enter (e.g. BUN145666/26-27)"
              className="h-12 text-base"
              data-testid="fdn-bundle-input"
            />
            <Button onClick={doSearch} disabled={loading} className="h-12 px-6 bg-[#2D5F4F] hover:bg-[#1A1F2E]" data-testid="fdn-search-btn">
              {loading ? 'Searching…' : 'Fetch'}
            </Button>
          </div>
        </div>

        {result && !result.found && (
          <div className="bg-white rounded-lg border border-[#E5DFD0] py-16 text-center text-[#5A5F6E]" data-testid="fdn-empty">
            No inward document found for <span className="font-mono font-semibold">{result.bundle_barcode}</span>.
          </div>
        )}

        {rows.length > 0 && (
          <div className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden" data-testid="fdn-result">
            <div className="px-4 py-3 border-b border-[#E5DFD0] flex items-center justify-between">
              <p className="font-semibold text-[#1A1F2E]">
                Bundle <span className="font-mono">{result.bundle_barcode}</span>
              </p>
              <Badge className="bg-indigo-100 text-indigo-700 border-0">{rows.length} document{rows.length === 1 ? '' : 's'}</Badge>
            </div>
            <div className="overflow-x-auto">
              <Table data-testid="fdn-table">
                <TableHeader>
                  <TableRow>
                    <TableHead><Landmark className="w-3.5 h-3.5 inline mr-1" />Document Number</TableHead>
                    <TableHead><Clock className="w-3.5 h-3.5 inline mr-1" />Gate Inward</TableHead>
                    <TableHead><Building2 className="w-3.5 h-3.5 inline mr-1" />Type of Section Receipt</TableHead>
                    <TableHead><PackageCheck className="w-3.5 h-3.5 inline mr-1" />Section Receipt Done</TableHead>
                    <TableHead><Landmark className="w-3.5 h-3.5 inline mr-1" />Handover to Accounts</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead className="text-center">Bundles</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={`${r.document_barcode}-${i}`} data-testid={`fdn-row-${r.document_barcode}`}>
                      <TableCell className="font-mono font-semibold text-[#2D5F4F]" data-testid={`fdn-docno-${i}`}>{r.document_barcode || '—'}</TableCell>
                      <TableCell className="text-sm" data-testid={`fdn-gate-${i}`}>{fmtDT(r.gate_inward_at)}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <span className="font-medium text-[#1A1F2E]" data-testid={`fdn-section-${i}`}>{r.section || '—'}</span>
                          <div className="flex items-center gap-1 flex-wrap">
                            {r.parcel_type && <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px] capitalize">{r.parcel_type}</Badge>}
                            <Badge className={`${r.section_receipt_status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'} border-0 text-[10px] capitalize`}>
                              {r.section_receipt_status?.replace('_', ' ')}
                            </Badge>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{fmtDT(r.section_receipt_completed_at)}</TableCell>
                      <TableCell className="text-sm" data-testid={`fdn-accounts-${i}`}>
                        {r.accounts_handover_at ? (
                          <div className="flex flex-col">
                            <span>{fmtDT(r.accounts_handover_at)}</span>
                            {r.accounts_received_by && <span className="text-[11px] text-[#5A5F6E]">by {r.accounts_received_by}</span>}
                          </div>
                        ) : (
                          <Badge className="bg-rose-100 text-rose-700 border-0">Not handed over</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{r.vendor_name || '—'}</TableCell>
                      <TableCell className="text-center">{r.bundle_count || 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default FetchDocumentNumber;
